import { useState } from 'react';
import { AppProvider, useAppState, useDispatch } from './state/AppContext';
import { AuthProvider } from './state/AuthContext';
import { TourProvider } from './state/TourContext';
import { CLEAR_ERROR } from './state/actions';
import LandingPage from './components/LandingPage';
import LoadingAnimation from './components/LoadingAnimation';
import GalaxyView from './components/GalaxyView';
import UniverseView from './components/UniverseView';
import CollisionView from './components/CollisionView';
import UniversePreview from './components/UniversePreview';
import CollisionPreview from './components/CollisionPreview';
import SharePage from './components/SharePage';
import ShareGalaxyPage from './components/ShareGalaxyPage';
import ShareUniversePage from './components/ShareUniversePage';
import ArtistLandingPage from './components/ArtistLandingPage';
import SimilarToPage from './components/SimilarToPage';
import ResetPasswordPage from './components/ResetPasswordPage';
import InvitePage from './components/InvitePage';
import AuthModal from './components/auth/AuthModal';
import ChooseUsernameModal from './components/auth/ChooseUsernameModal';
import IosAppBanner from './components/IosAppBanner';
import NotificationManager from './components/NotificationManager';
import './App.css';

function AppContent() {
  const { phase, error } = useAppState();
  const dispatch = useDispatch();

  // Simple path-based routing for share pages
  const [sharePageId] = useState(() => {
    const match = window.location.pathname.match(/^\/p\/([a-f0-9-]+)$/);
    return match ? match[1] : null;
  });

  // Galaxy share page routing
  const [galaxyShareId] = useState(() => {
    const match = window.location.pathname.match(/^\/galaxy\/([a-f0-9-]+)$/);
    return match ? match[1] : null;
  });

  // Universe share page routing
  const [universeShareId] = useState(() => {
    const match = window.location.pathname.match(/^\/universe\/([a-f0-9-]+)$/);
    return match ? match[1] : null;
  });

  // Artist landing page routing
  const [artistSlug] = useState(() => {
    const match = window.location.pathname.match(/^\/artist\/([a-z0-9-]+)$/);
    return match ? match[1] : null;
  });

  // Similar-to SEO page routing
  const [similarToSlug] = useState(() => {
    const match = window.location.pathname.match(/^\/similar-to\/([a-z0-9-]+)$/);
    return match ? match[1] : null;
  });

  // Invite page routing
  const [inviteCode] = useState(() => {
    const match = window.location.pathname.match(/^\/invite\/([A-Za-z0-9_-]+)$/);
    return match ? match[1] : null;
  });

  // Password reset page routing
  const [resetToken] = useState(() => {
    if (window.location.pathname === '/reset-password') {
      const params = new URLSearchParams(window.location.search);
      return params.get('token') || null;
    }
    return null;
  });

  // Render share page if URL matches /p/:id
  if (sharePageId) {
    return <SharePage playlistId={sharePageId} />;
  }

  // Render galaxy share page if URL matches /galaxy/:id
  if (galaxyShareId) {
    return <ShareGalaxyPage galaxyId={galaxyShareId} />;
  }

  // Render universe share page if URL matches /universe/:id
  if (universeShareId) {
    return <ShareUniversePage universeId={universeShareId} />;
  }

  // Render artist landing page if URL matches /artist/:slug
  if (artistSlug) {
    return <ArtistLandingPage slug={artistSlug} />;
  }

  // Render similar-to SEO page if URL matches /similar-to/:slug
  if (similarToSlug) {
    return <SimilarToPage slug={similarToSlug} />;
  }

  // Render invite page if URL matches /invite/:code
  if (inviteCode) {
    return <InvitePage inviteCode={inviteCode} />;
  }

  // Render reset password page if URL matches /reset-password?token=...
  if (resetToken) {
    return <ResetPasswordPage token={resetToken} />;
  }

  return (
    <div className="app">
      {error && (
        <div className="error-banner">
          <p>{error}</p>
          <button onClick={() => dispatch({ type: CLEAR_ERROR })}>
            &times;
          </button>
        </div>
      )}

      {phase === 'inputting' && <LandingPage />}
      {phase === 'loading' && <LoadingAnimation />}
      {phase === 'viewing' && <GalaxyView />}
      {phase === 'viewing-universe' && <UniverseView />}
      {phase === 'viewing-universe-preview' && <UniversePreview />}
      {phase === 'viewing-collision' && <CollisionView />}
      {phase === 'viewing-collision-preview' && <CollisionPreview />}

      <AuthModal />
      <ChooseUsernameModal />
      <NotificationManager />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppProvider>
        <TourProvider>
          <IosAppBanner />
          <AppContent />
        </TourProvider>
      </AppProvider>
    </AuthProvider>
  );
}

export default App;
